import { Component } from '@theme/component';

export class ProductColorSiblings extends Component {
  static CONTRACT_VERSION = '1';
  static SECTION_ID = 'custom_color-siblings-results';
  static MAX_CARD_VISIBLE = 5;
  static MAX_PDP_VISIBLE = 50;
  static CACHE_MAX_ENTRIES = 50;
  static CACHE_TTL_MS = 5 * 60 * 1000;
  static NEGATIVE_CACHE_TTL_MS = 30 * 1000;
  static FETCH_TIMEOUT_MS = 5 * 1000;
  static MAX_CONCURRENT = 4;

  static requestQueue = [];
  static activeRequestCount = 0;
  static inflightRequests = new Map();
  static cache = new Map();

  constructor() {
    super();
    this.family = '';
    this.productId = '';
    this.context = 'card';
    this.endpoint = '';
    this.listLabel = 'Color';
    this.soldOutLabel = '';
    this.overflowLabelTemplate = '%count% colores adicionales';
    this.siblings = [];
    this._loadPromise = null;
    this._pointerHandler = null;
    this._focusHandler = null;
    this._intentTarget = null;
    this._observer = null;
    this._observedElement = null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.family = (this.dataset.family || '').trim();
    this.productId = this.dataset.productId || '';
    this.context = this.dataset.context || 'card';
    this.endpoint = this.dataset.endpoint || '';
    this.listLabel = (this.dataset.listLabel || this.listLabel).trim() || this.listLabel;
    this.soldOutLabel = (this.dataset.soldOutLabel || '').trim();
    this.overflowLabelTemplate = (this.dataset.overflowLabelTemplate || this.overflowLabelTemplate).trim() || this.overflowLabelTemplate;

    if (!this.productId || !this.endpoint || !ProductColorSiblings.isValidFamily(this.family)) {
      this.remove();
      return;
    }

    if (this.context === 'pdp') {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.loadSiblings(), { timeout: 1500 });
      } else {
        setTimeout(() => this.loadSiblings(), 0);
      }
      return;
    }

    this.setupDeferredLoading();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupDeferredLoading();
    this._loadPromise = null;
  }

  cleanupDeferredLoading() {
    if (this._intentTarget) {
      if (this._pointerHandler) {
        this._intentTarget.removeEventListener('pointerenter', this._pointerHandler);
      }
      if (this._focusHandler) {
        this._intentTarget.removeEventListener('focusin', this._focusHandler);
      }
      this._intentTarget = null;
    }
    if (this._pointerHandler) {
      this._pointerHandler = null;
    }
    if (this._focusHandler) {
      this._focusHandler = null;
    }
    if (this._observer && this._observedElement && typeof this._observer.unobserve === 'function') {
      this._observer.unobserve(this._observedElement);
    }
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._observedElement) {
      this._observedElement = null;
    }
  }

  static isValidFamily(family) {
    if (typeof family !== 'string' || !family || family.length > 200) return false;
    const hasControl = /[\x00-\x1F\x7F]/.test(family);
    return !hasControl;
  }

  setupDeferredLoading() {
    const isTouchDevice = typeof window.matchMedia === 'function' && !window.matchMedia('(hover: hover)').matches;

    if (isTouchDevice && typeof IntersectionObserver !== 'undefined') {
      this.setupIntersectionObserver();
    } else {
      const card = this.closest('product-card');
      if (!card) {
        this.loadSiblings();
        return;
      }

      this._pointerHandler = this.handlePointerEnter.bind(this);
      this._focusHandler = this.handleFocusIn.bind(this);
      this._intentTarget = card;

      card.addEventListener('pointerenter', this._pointerHandler, { once: true });
      card.addEventListener('focusin', this._focusHandler, { once: true });
    }
  }

  handlePointerEnter() {
    this.cleanupDeferredLoading();
    this.loadSiblings();
  }

  handleFocusIn() {
    this.cleanupDeferredLoading();
    this.loadSiblings();
  }

  setupIntersectionObserver() {
    const card = this.closest('product-card');
    if (!card) {
      this.loadSiblings();
      return;
    }

    this._observedElement = card;
    this._observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.cleanupDeferredLoading();
            this.loadSiblings();
            break;
          }
        }
      },
      { rootMargin: '100px' }
    );

    this._observer.observe(card);
  }

  async loadSiblings() {
    if (this.siblings.length > 0) return;
    if (this._loadPromise) return this._loadPromise;

    this._loadPromise = (async () => {
      const cacheKey = ProductColorSiblings.getCacheKey(
        this.endpoint,
        this.family,
        window.location.origin,
        window.Shopify?.country || ''
      );
      const cached = ProductColorSiblings.getFromCache(cacheKey);
      const records = cached.hit
        ? cached.data
        : await ProductColorSiblings.fetchWithSemaphore(
          ProductColorSiblings.buildRequestUrl(this.endpoint, this.family, window.location.origin),
          cacheKey,
          { family: this.family, origin: window.location.origin }
        );

      if (!records) return;

      const validated = ProductColorSiblings.validateRecords(records, {
        family: this.family,
        activeProductId: this.productId,
      });

      this.siblings = ProductColorSiblings.dedupeAndSort(validated, this.productId);
      this.render();
    })()
      .catch((error) => {
        console.warn('[color-siblings] Load failed:', error.message);
      })
      .finally(() => {
        this._loadPromise = null;
      });

    return this._loadPromise;
  }

  static buildEndpointUrl(endpoint, origin = window.location.origin) {
    const url = new URL(endpoint, origin);
    if (url.origin !== origin) {
      throw new Error('Invalid endpoint origin');
    }

    const path = url.pathname.replace(/\/+$/, '') || '/';
    if (!/(^|\/)collections\/all$/.test(path)) {
      throw new Error('Invalid endpoint path');
    }

    return url;
  }

  static buildRequestUrl(endpoint, family, origin = window.location.origin) {
    const normalizedFamily = (family || '').trim();
    if (!ProductColorSiblings.isValidFamily(normalizedFamily)) {
      throw new Error('Invalid family');
    }

    const url = ProductColorSiblings.buildEndpointUrl(endpoint, origin);
    url.searchParams.set('filter.p.m.custom.reference', normalizedFamily);
    url.searchParams.set('section_id', ProductColorSiblings.SECTION_ID);
    return url;
  }

  static getCacheKey(endpoint, family, origin = window.location.origin, country = window.Shopify?.country || '') {
    const url = ProductColorSiblings.buildEndpointUrl(endpoint, origin);
    const path = url.pathname.replace(/\/+$/, '') || '/';
    return [origin, path, country || '', (family || '').trim()].join('|');
  }

  static async fetchWithSemaphore(url, cacheKey, context) {
    const existing = ProductColorSiblings.inflightRequests.get(cacheKey);
    if (existing) return existing;

    const promise = new Promise((resolve, reject) => {
      ProductColorSiblings.requestQueue.push({ url, cacheKey, context, resolve, reject });
      ProductColorSiblings.processQueue();
    }).finally(() => {
      ProductColorSiblings.inflightRequests.delete(cacheKey);
    });

    ProductColorSiblings.inflightRequests.set(cacheKey, promise);
    return promise;
  }

  static processQueue() {
    while (
      ProductColorSiblings.requestQueue.length > 0 &&
      ProductColorSiblings.activeRequestCount < ProductColorSiblings.MAX_CONCURRENT
    ) {
      const next = ProductColorSiblings.requestQueue.shift();
      if (!next) break;

      ProductColorSiblings.activeRequestCount++;
      ProductColorSiblings.doFetch(next.url, next.cacheKey, next.context)
        .then(next.resolve, next.reject)
        .finally(() => {
          ProductColorSiblings.activeRequestCount--;
          ProductColorSiblings.processQueue();
        });
    }
  }

  static async doFetch(url, cacheKey, context) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ProductColorSiblings.FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(url.toString(), {
        signal: controller.signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      const siblings = ProductColorSiblings.parseResponse(html, context);

      ProductColorSiblings.addToCache(cacheKey, siblings);
      return siblings;
    } catch (error) {
      ProductColorSiblings.addToCache(cacheKey, null);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static validateResponseMeta({ version, truncated }) {
    if (version !== ProductColorSiblings.CONTRACT_VERSION) {
      throw new Error(`Invalid version: ${version}`);
    }
    if (truncated) {
      throw new Error('Results truncated');
    }
  }

  static parseResponse(html, context = {}) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const root = doc.querySelector('[data-color-siblings-results]');
    if (!root) {
      throw new Error('No results root');
    }

    ProductColorSiblings.validateResponseMeta({
      version: root.dataset.version,
      truncated: root.dataset.truncated === 'true',
    });

    const requestedFamily = (context.family || '').trim();
    const origin = context.origin || window.location.origin;
    const links = root.querySelectorAll('a.color-sibling');
    const siblings = [];

    for (const link of links) {
      const productId = link.dataset.productId;
      const linkFamily = (link.dataset.family || '').trim();
      const refPantone = (link.dataset.refPantone || '').trim();
      const available = link.dataset.available === 'true';
      const url = link.dataset.url || '';
      const title = link.dataset.title || '';
      const image = link.dataset.image || '';
      const imageAlt = link.dataset.imageAlt || '';
      const imageWidth = parseInt(link.dataset.imageWidth, 10) || 0;
      const imageHeight = parseInt(link.dataset.imageHeight, 10) || 0;

      if (linkFamily !== requestedFamily) {
        throw new Error(`Family mismatch: ${linkFamily} !== ${requestedFamily}`);
      }
      if (!productId || !/^\d+$/.test(productId)) continue;
      if (!title) continue;

      const parsedUrl = ProductColorSiblings.normalizeProductUrl(url, origin);
      if (!parsedUrl) continue;

      const safeImage = image && ProductColorSiblings.isValidImageUrl(image, origin) ? image : '';

      const sibling = {
        productId,
        family: linkFamily,
        refPantone,
        available,
        url: parsedUrl,
        title,
        image: safeImage,
        imageAlt: safeImage ? imageAlt : '',
        imageWidth: safeImage ? imageWidth : 0,
        imageHeight: safeImage ? imageHeight : 0,
      };

      siblings.push(sibling);
    }

    return ProductColorSiblings.validateRecords(siblings, { family: requestedFamily });
  }

  static validateRecords(records, { family, activeProductId = '' } = {}) {
    const requestedFamily = (family || '').trim();
    if (!ProductColorSiblings.isValidFamily(requestedFamily)) {
      throw new Error('Invalid family');
    }
    if (!Array.isArray(records)) {
      throw new Error('Invalid records');
    }

    for (const record of records) {
      const recordFamily = (record.family || '').trim();
      if (recordFamily !== requestedFamily) {
        throw new Error(`Family mismatch: ${recordFamily} !== ${requestedFamily}`);
      }
    }

    if (activeProductId && !records.some((record) => record.productId === activeProductId)) {
      throw new Error('Active product not in response');
    }

    return records.slice();
  }

  static normalizeProductUrl(raw, origin = window.location.origin) {
    try {
      const url = new URL(raw, origin);

      if (url.origin !== origin) return null;
      if (!ProductColorSiblings.isProductPath(url.pathname)) return null;

      return url.pathname + url.search + url.hash;
    } catch {
      return null;
    }
  }

  static isProductPath(pathname) {
    const parts = pathname.split('/').filter(Boolean);
    const productIndex = parts.indexOf('products');

    if (productIndex === -1 || productIndex !== parts.length - 2) return false;
    if (productIndex === 0) return true;
    return productIndex === 1 && /^[a-z]{2}(?:-[a-z0-9]{2,})?$/i.test(parts[0]);
  }

  static isValidImageUrl(raw, origin = window.location.origin) {
    try {
      const url = new URL(raw, origin);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
      if (url.origin === origin) return true;

      const host = url.hostname.toLowerCase();
      return host === 'cdn.shopify.com' || host === 'shopifycdn.net' || host.endsWith('.shopifycdn.net');
    } catch {
      return false;
    }
  }

  static getFromCache(key) {
    const entry = ProductColorSiblings.cache.get(key);
    if (!entry) return { hit: false, data: null };

    const now = Date.now();
    const ttl = entry.negative ? ProductColorSiblings.NEGATIVE_CACHE_TTL_MS : ProductColorSiblings.CACHE_TTL_MS;

    if (now - entry.timestamp > ttl) {
      ProductColorSiblings.cache.delete(key);
      return { hit: false, data: null };
    }

    return { hit: true, data: entry.data };
  }

  static addToCache(key, data) {
    if (!ProductColorSiblings.cache.has(key) && ProductColorSiblings.cache.size >= ProductColorSiblings.CACHE_MAX_ENTRIES) {
      let oldestKey = null;
      let oldestTime = Infinity;

      for (const [k, v] of ProductColorSiblings.cache) {
        if (v.timestamp < oldestTime) {
          oldestTime = v.timestamp;
          oldestKey = k;
        }
      }

      if (oldestKey) {
        ProductColorSiblings.cache.delete(oldestKey);
      }
    }

    ProductColorSiblings.cache.set(key, {
      data,
      timestamp: Date.now(),
      negative: data === null
    });
  }

  static dedupeAndSort(records, activeProductId = '') {
    const seen = new Map();

    for (const record of records) {
      const key = record.refPantone ? `pantone:${record.refPantone}` : `product:${record.productId}`;
      const current = seen.get(key);
      if (!current || record.productId === activeProductId) {
        seen.set(key, record);
      }
    }

    const deduped = Array.from(seen.values());
    deduped.sort((a, b) => {
      const aRef = a.refPantone || '';
      const bRef = b.refPantone || '';
      if (aRef && bRef) return aRef.localeCompare(bRef);
      if (aRef) return -1;
      if (bRef) return 1;
      return a.productId.localeCompare(b.productId);
    });

    return deduped.map((record) => ({
      ...record,
      isActive: record.productId === activeProductId,
    }));
  }

  static selectVisible(records, activeProductId, max) {
    if (max <= 0) return { visible: [], overflow: records.length };

    const visible = records.slice(0, max);
    const activeIndex = records.findIndex((record) => record.productId === activeProductId);
    if (activeIndex >= max) {
      visible[visible.length - 1] = records[activeIndex];
    }

    return {
      visible,
      overflow: Math.max(0, records.length - max),
    };
  }

  static getLinkLabel(sibling, soldOutLabel = '') {
    if (sibling.available || !soldOutLabel) return sibling.title;
    return `${sibling.title} - ${soldOutLabel}`;
  }

  static formatOverflowLabel(template, count) {
    const label = template || '%count% colores adicionales';
    return label.replace('%count%', String(count));
  }

  static shouldRender(records) {
    return records.length > 0;
  }

  render() {
    if (!ProductColorSiblings.shouldRender(this.siblings)) {
      this.classList.add('product-color-siblings__hidden');
      return;
    }

    this.classList.remove('product-color-siblings__hidden');

    const maxVisible = this.context === 'pdp' ? ProductColorSiblings.MAX_PDP_VISIBLE : ProductColorSiblings.MAX_CARD_VISIBLE;
    const { visible, overflow } = ProductColorSiblings.selectVisible(this.siblings, this.productId, maxVisible);

    const list = document.createElement('ul');
    list.className = 'product-color-siblings__list';
    list.setAttribute('role', 'list');
    list.setAttribute('aria-label', this.listLabel);

    for (const sibling of visible) {
      const item = this.createSiblingElement(sibling);
      list.appendChild(item);
    }

    if (this.context === 'card' && overflow > 0) {
      const overflowItem = document.createElement('li');
      overflowItem.className = 'product-color-siblings__item';
      overflowItem.setAttribute('role', 'listitem');
      const overflowSpan = document.createElement('span');
      overflowSpan.className = 'product-color-siblings__overflow';
      overflowSpan.textContent = `+${overflow}`;
      const overflowLabel = ProductColorSiblings.formatOverflowLabel(this.overflowLabelTemplate, overflow);
      overflowSpan.title = overflowLabel;
      overflowSpan.setAttribute('aria-label', overflowLabel);
      overflowItem.appendChild(overflowSpan);
      list.appendChild(overflowItem);
    }

    const oldList = this.querySelector('.product-color-siblings__list');
    if (oldList) {
      oldList.replaceWith(list);
    } else {
      this.appendChild(list);
    }
  }

  createSiblingElement(sibling) {
    const item = document.createElement('li');
    item.className = 'product-color-siblings__item';
    item.setAttribute('role', 'listitem');

    const link = document.createElement('a');
    link.className = 'product-color-siblings__link';
    const linkLabel = ProductColorSiblings.getLinkLabel(sibling, this.soldOutLabel);
    if (sibling.isActive) {
      link.classList.add('product-color-siblings__link--active');
      link.setAttribute('aria-current', 'page');
    }
    if (!sibling.available) {
      link.classList.add('product-color-siblings__link--sold-out');
    }
    link.href = sibling.url;
    link.title = linkLabel;
    link.setAttribute('aria-label', linkLabel);

    if (sibling.image) {
      const img = document.createElement('img');
      img.className = 'product-color-siblings__image';
      img.src = sibling.image;
      img.alt = sibling.imageAlt || sibling.title;
      img.loading = 'lazy';
      if (sibling.imageWidth) img.width = sibling.imageWidth;
      if (sibling.imageHeight) img.height = sibling.imageHeight;
      link.appendChild(img);
    }

    item.appendChild(link);
    return item;
  }
}

if (!customElements.get('product-color-siblings')) {
  customElements.define('product-color-siblings', ProductColorSiblings);
}
